import { Alert, Box, Container, Header, Icon } from '@amzn/awsui-components-core';
import {
  Attachment,
  AttachmentScanResult,
  ContentType,
} from '@amzn/awscat-pattern-library-service-model';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import constants from '../../common/constants';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import '../../resources/styles/scss/components/common/RenderAttachments.css';
import AppContextProps from '../../contexts/AppContextProps';
import { AttachmentListItem } from './AttachmentListItem';
import FileUpload from './FileUpload';
import { allowedPatternAttachmentTypes } from '../../api/UtilsApi';
import { isAdminOrTechWriter } from '../utils/ContentDataUtils';
import DiagramGuidelinesDeck from '../content/DiagramGuidelinesDeck';
import { SpaceBetween } from '@amzn/awsui-components-core';

interface Props extends AppContextProps {
  contentId: string;
  attachmentIds: string[];
  readOnly: boolean;
  onUpdate?: (attachment: Attachment) => void;
  onDelete?: (attachmentId: string) => void;
  tabView?: boolean;
  contentType?: ContentType;
}

export const AttachmentList: FunctionComponent<Props> = (props) => {
  const { contentType } = props;
  // @ts-expect-error TS2339 - strict migration
  const { userProfile } = props.appContext.authnInfo;

  const mplService = MplServiceClientFactory.create();
  const flashbarMessageBroker = FlashbarMessageBroker.create();

  const [isUploading, setIsUploading] = useState(false);
  const [attachments, setAttachments] = useState(new Map<string, Attachment>());

  // Ids of the attachments that have just been uploaded using the file upload control
  const [newAttachmentIds, setNewAttachmentIds] = useState<string[]>([]);

  if (!Array.isArray(props.attachmentIds)) {
    return null;
  }

  // Ids of attachments to be rendered.
  // It is an union of the existing and new attachments.
  const attachmentIds = new Set([...props.attachmentIds, ...newAttachmentIds]);

  const isValidPatternAttachmentType = (fileName: string) => {
    return allowedPatternAttachmentTypes.some((type) => fileName.endsWith(type));
  };

  const shouldRestrictFileType = () => {
    return contentType === ContentType.Pattern && !isAdminOrTechWriter(userProfile);
  };

  const uploadFile = async (file: any) => {
    if (!file) {
      return;
    }

    // Check for file type if it is pattern
    if (shouldRestrictFileType() && !isValidPatternAttachmentType(file.name)) {
      flashbarMessageBroker.pushErrorMessage(constants.UPLOAD_ATTACHMENT_FILE_TYPE_INVALID);
      return;
    }

    // Limit uploads to 10MB
    if (file.size > 10 * 1024 * 1024) {
      flashbarMessageBroker.pushErrorMessage(constants.UPLOAD_ATTACHMENT_FILE_TOO_LARGE);
      return;
    }

    setIsUploading(true);

    try {
      const attachmentInfo = await mplService.putContentAttachment(contentId, file);
      setNewAttachmentIds((prev) => [...prev, attachmentInfo.attachmentId]);
    } catch (e) {
      flashbarMessageBroker.pushErrorMessage(constants.UPLOAD_ATTACHMENT_ERROR);
    } finally {
      setIsUploading(false);
    }
  };

  const updateAttachment = (attachment: Attachment) => {
    const updated = new Map(attachments);
    updated.set(attachment.attachmentId, attachment);
    setAttachments(updated);
    if (props.onUpdate) {
      props.onUpdate(attachment);
    }
  };

  const removeAttachment = (attachmentId: string) => {
    // this might be a newly uploaded attachment,
    // ensure it is removed from the new attachments list first
    setNewAttachmentIds((prev) => prev.filter((id) => id !== attachmentId));

    // Remove from the attachments map so the counter updates
    setAttachments((prev) => {
      const updated = new Map(prev);
      updated.delete(attachmentId);
      return updated;
    });

    if (props.onDelete) {
      props.onDelete(attachmentId);
    }
  };

  const { contentId, readOnly } = props;

  // @ts-expect-error TS7030 - strict migration
  const renderUploadControls = () => {
    if (!readOnly) {
      const allowedTypes = shouldRestrictFileType() ? allowedPatternAttachmentTypes.join(', ') : '';
      return (
        <FileUpload
          handleFileUpload={uploadFile}
          isFileUploading={isUploading}
          customButtonText={constants.ADD_ATTACHMENT}
          acceptanceType={allowedTypes}
        />
      );
    }
  };

  const renderAttachments = () => {
    if (attachmentIds.size === 0) {
      const noAttachmentsClassName = props.tabView ? 'no-attachments-tab-view' : '';
      const noAttachmentsText = props.tabView
        ? constants.ATTACHMENTS_NO_ATTACHMENTS_TAB_VIEW_INFO
        : constants.ATTACHMENTS_NO_ATTACHMENTS_INFO;
      return <p className={noAttachmentsClassName}>{noAttachmentsText}</p>;
    }

    return [...attachmentIds].map((attachmentId) => {
      return (
        <AttachmentListItem
          key={attachmentId}
          contentId={contentId}
          attachmentId={attachmentId}
          readOnly={readOnly}
          onUpdate={updateAttachment}
          onDelete={removeAttachment}
        />
      );
    });
  };

  // @ts-expect-error TS7030 - strict migration
  const renderScanResultsInfo = () => {
    if (
      Array.from(attachments.values()).some((a) => a.scanResult === AttachmentScanResult.INFECTED)
    ) {
      return (
        <div className={'attachment-infected-warning-container'}>
          <div className={'attachment-infected-warning-icon'}>
            <Icon size="big" name="status-warning" variant="warning" />
          </div>
          <div className={'attachment-infected-warning-text'}>
            {constants.ATTACHMENTS_INFECTED_INFO}
          </div>
        </div>
      );
    }
  };

  const componentBody = props.tabView ? (
    <div>
      {renderScanResultsInfo()}
      {renderAttachments()}
    </div>
  ) : (
    <div>
      {renderScanResultsInfo()}
      {renderAttachments()}
      {renderUploadControls()}
    </div>
  );

  const attachmentListColumnView = readOnly ? (
    <article id="attachment-list">
      <header>
        <Box variant="h5">{constants.ATTACHMENTS_HEADER}</Box>
      </header>
      {componentBody}
    </article>
  ) : (
    <Container header={<Header variant="h3">{constants.ATTACHMENTS_HEADER}</Header>}>
      {componentBody}
    </Container>
  );

  const attachmentInstruction = shouldRestrictFileType()
    ? constants.ATTACHMENTS_INSTRUCTION_PATTERN
    : constants.ATTACHMENTS_INSTRUCTION_GUIDE_STRATEGY;

  const attachmentListTabView = (
    <Container
      header={
        <Header
          variant="h3"
          counter={`(${attachments.size})`}
          description={attachmentInstruction}
          actions={
            <SpaceBetween direction="horizontal" size={'xs'}>
              {renderUploadControls()}
              <DiagramGuidelinesDeck isButton />
            </SpaceBetween>
          }
        >
          {constants.ATTACHMENTS_HEADER}
        </Header>
      }
    >
      {componentBody}
    </Container>
  );

  const attachmentList = props.tabView ? attachmentListTabView : attachmentListColumnView;

  const attachmentWarning = <Alert type={'warning'}>{constants.ATTACHMENTS_WARNING_TEXT}</Alert>;

  return (
    <SpaceBetween size="m">
      {attachmentList}
      {readOnly ? null : attachmentWarning}
    </SpaceBetween>
  );
};
